import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { InfoTip } from '../shared/InfoTip';
import type { DailyChartPoint } from '../../services/dashboardService';
import type { ChartDays } from '../../hooks/useDailyChart';

interface DailySmsChartProps {
  days: ChartDays;
  onDaysChange: (days: ChartDays) => void;
  data: DailyChartPoint[];
  loading: boolean;
  error: string | null;
}

/**
 * SMS sent per day. There is no visits-completed series: DHIS2 records a visit
 * about a month late, so the two would sit out of phase on one day axis.
 */
export const DailySmsChart: React.FC<DailySmsChartProps> = ({ days, onDaysChange, data, loading, error }) => {
  const theme = useTheme();

  return (
    <Card variant="outlined" elevation={0} sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Daily SMS Sent
            <InfoTip
              label="Daily SMS Sent"
              text="Reminder messages sent on each day. Useful for spotting a day when sending stopped."
            />
          </Typography>
          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={(_, val: ChartDays | null) => val && onDaysChange(val)}
            size="small"
          >
            <ToggleButton value={7}>7 Days</ToggleButton>
            <ToggleButton value={30}>30 Days</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : loading ? (
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ borderRadius: 1 }} />
        ) : data.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No data available yet</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 0 }} barCategoryGap="28%">
              <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeOpacity={0.5} />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => format(parseISO(d), 'MMM d')}
                fontSize={12}
                stroke={theme.palette.text.secondary}
                tickLine={false}
                axisLine={false}
                dy={6}
              />
              <YAxis
                allowDecimals={false}
                fontSize={12}
                stroke={theme.palette.text.secondary}
                tickLine={false}
                axisLine={false}
                width={36}
              />
              <RechartsTooltip
                cursor={{ fill: theme.palette.action.hover }}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  boxShadow: theme.shadows[3],
                  fontSize: 12,
                }}
                labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                itemStyle={{ color: theme.palette.text.secondary }}
                labelFormatter={(d) => format(parseISO(d as string), 'PPP')}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="smsSent" name="SMS Sent" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
